import type { AppConfig } from '../types/esusuSdk';
import highFive from '../assets/high-five.svg';
import {
  footerConfig,
  formStyles,
  globalStyle,
  headerConfig,
  initialLoadingIndicator,
  mobileFooterConfig,
  mobileGlobalStyle,
  modalStyles,
  features as sharedFeatures,
  theme,
} from '../config/esusuSharedConfig';

import { COMET_1, getCurrentEnvForClientSide, PRODUCTION, STAGING } from '@zg-rentals/rental-platform-config';

const PRODUCTION_ESUSU_BASE_URL = 'https://raas-app.esusu.io';
const SANDBOX_ESUSU_BASE_URL = 'https://raas-app.sandbox.esusu.io';

const PRODUCTION_PUBLIC_KEY = '0WHGXAN2GDWL0C9P880WKX87G6YCU33';
const QA_PUBLIC_KEY = 'A7XK3FZL9B8T2WQC4YD0MNR5G1VEJH6S';
// QA public key will work for localhost and QA, but leaving this here in case it's needed in the future
// const DEV_PUBLIC_KEY = 'Q4MZ1LKE7Y8C5JRA2V9XWT6H0N3SDFG';

export const getEsusuBaseUrl = () => {
  if (typeof window !== 'undefined') {
    const env = getCurrentEnvForClientSide();
    if (env === PRODUCTION) {
      return PRODUCTION_ESUSU_BASE_URL;
    }
  }
  return SANDBOX_ESUSU_BASE_URL;
};

export const getPublicKeyForEnv = () => {
  if (typeof window !== 'undefined') {
    const env = getCurrentEnvForClientSide();
    if (env === PRODUCTION) {
      return PRODUCTION_PUBLIC_KEY;
    }
  }
  return QA_PUBLIC_KEY;
};

const getRootPath = () => {
  if (typeof window !== 'undefined') {
    const env = getCurrentEnvForClientSide();
    if (env === PRODUCTION) {
      return 'https://www.zillow.com';
    }
    if (env === COMET_1 || env === STAGING) {
      return 'https://www.qa.zillow.net';
    }
  }
  return 'https://localhost:3000';
};

export const esusuConfig: AppConfig = {
  mobile: {
    components: {
      global: {
        type: 'global',
        style: mobileGlobalStyle,
        forms: {
          style: {
            ...formStyles,
            transactionListContainer: {
              marginTop: '48px',
            },
            transactionFilterContainer: {
              marginTop: '48px',
            },
          },
        },
        modal: {
          style: modalStyles,
        },
        header: headerConfig,
        footer: {
          ...mobileFooterConfig,
          style: {
            ...(mobileFooterConfig.style || {}),
            content: {
              ...(mobileFooterConfig.style?.content || {}),
              color: 'green',
            },
          },
        },
      },
    },
  },
  theme,
  features: {
    ...sharedFeatures,
    onboardingCompleteCTALinkUrl: getRootPath() + '/rental/creditclimb?enrollmentCompleted=true',
    onboardingFirstPageBackButtonLinkUrl:
      getRootPath() + '/rental/creditclimb/enroll?ignoreEnrollment=true&step=privacyNotice',
    externalImages: {
      onboarding: {
        thanks: {
          hero: {
            src: highFive.src,
            alt: '',
            style: {
              container: {
                display: 'flex',
              },
              image: {
                width: '120px',
                height: '120px',
              },
            },
          },
        },
      },
    },
  },
  components: {
    global: {
      progressBar: {
        features: {
          fullWidth: true,
        },
      },
      style: {
        ...globalStyle,
        container: {
          ...globalStyle.container,
          margin: '9px 5rem',
        },
      },
      initialLoadingIndicator,
      loadingOverlay: {
        style: {
          loadingSpinner: {
            color: '#0041D9',
          },
        },
      },
      type: 'global',
      forms: {
        style: {
          ...formStyles,
          transactionListContainer: {
            marginTop: '48px',
          },
          transactionFilterContainer: {
            marginTop: '48px',
          },
        },
      },
      header: headerConfig,
      footer: footerConfig,
      modal: {
        style: modalStyles,
      },
    },
    flows: [
      {
        type: 'flow',
        id: 'onboarding',
        sections: [
          {
            type: 'section',
            id: 'name',
            style: {
              container: {
                fontSize: '18px',
              },
            },
          },
          {
            type: 'section',
            id: 'link_bank',
            footer: {
              style: {
                nextButton: {
                  width: '260px',
                  backgroundColor: '#0041D9',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '12px',
                  padding: '12px 16px',
                  fontSize: '16px',
                  fontWeight: 700,
                  height: '48px',
                },
              },
            },
          },
          {
            type: 'section',
            id: 'thanks',
            footer: {
              style: {
                content: {
                  display: 'flex',
                  margin: '0px 5rem',
                  width: '100%',
                  justifyContent: 'flex-end',
                },
                nextButton: {
                  width: '170px',
                  backgroundColor: '#0041D9',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '12px',
                  padding: '12px 16px',
                  fontSize: '16px',
                  fontWeight: 700,
                  height: '48px',
                },
              },
            },
          },
        ],
      },
    ],
    widgets: [
      {
        type: 'widget',
        id: 'reportingHistory',
        style: {
          container: {
            padding: '1rem',
            width: '420px',
          },
        },
      },
      {
        type: 'widget',
        id: 'creditScore',
        style: {
          container: {
            padding: '1rem',
            height: '320px',
            width: '340px',
          },
        },
      },
      {
        type: 'widget',
        id: 'membershipDetails',
        style: {
          container: {
            padding: '1rem',
            width: '300px',
          },
        },
      },
    ],
  },
  text: {
    'onboarding.name.title': 'Enter your legal name',
    'onboarding.name.prompt': '',
    'onboarding.name.firstNameLabel': 'First name',
    'onboarding.name.lastNameLabel': 'Last name',
    'onboarding.identity.title': 'Verify your identity',
    'onboarding.identity.prompt':
      "To begin reporting your rent payments, we need to verify your identity. We'll use these details to make sure you get credit for paying rent.",
    'onboarding.identity.ssnLabel': 'Social security number',
    'onboarding.identity.dateOfBirthLabel': 'Date of birth',
    'onboarding.identity.disclaimer':
      'Protecting your data is important to us. Your data is always encrypted when it is passed from Zillow to Esusu.',
    'onboarding.address.title': 'Enter the address of your current lease',
    'onboarding.address.prompt': '',
    'onboarding.address.streetLabel': 'Street address',
    'onboarding.address.unitLabel': 'Unit number',
    'onboarding.address.postalCodeLabel': 'ZIP Code',
    'onboarding.lease.title': 'Tell us about your current lease',
    'onboarding.lease.noEndDateLabel': "This lease doesn't have an end date or is month-to-month.",
    'onboarding.lease.prompt': 'This helps us accurately report your rent payments.',
    'onboarding.lease.startDateLabel': 'Lease start date',
    'onboarding.lease.endDateLabel': 'Lease end date',
    'onboarding.linkBank.prompt':
      'Next, Plaid will securely connect to the bank account that you use to pay rent each month.',
    'onboarding.transactions.foundPrompt':
      'We identified these transactions from your bank account. Select some rent payments from the past 24 months.',
    'misc.continueLabel': 'Next',
    'widgets.creditScore.title': 'Credit score',
    'onboarding.thanks.continue': 'Go to dashboard',
    'onboarding.transactions.confirmButton': 'Next',
    'onboarding.thanks.summaryTitle': '',
    'onboarding.thanks.nextSteps': 'View your rent reporting status and make changes in your CreditClimb dashboard.',
    'onboarding.payment.rentReporting': 'CreditClimb',
    'onboarding.thanks.comeBackMessage': '',
    'onboarding.phone.consent':
      'I consent to receive calls and text messages from Esusu, Inc. at the number provided, including using an automatic telephone dialing system or artificial or prerecorded voice. Consent not a condition of purchase. Msg & data rates may apply. Msg frequency varies. You may revoke this consent at any time.',
    'onboarding.checkout.prompt': 'Add your payment details to finish subscribing to CreditClimb.',
  },
};
